import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Notifications as NotificationsIcon,
  Gavel as GavelIcon,
  TrendingUp as TrendingUpIcon,
  Cancel as CancelIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { useUnifiedTranslation } from '../../hooks/useUnifiedTranslation';
import { managerApi } from '../../services/api/manager.api';
import { PAGE_SIZE } from '../../constants/pagination';
import { ModerationStatus } from '../../types/moderation';
import { DataTable } from '../ui/DataTable';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`manager-tabpanel-${index}`}
      aria-labelledby={`manager-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const ManagerDashboard: React.FC = () => {
  const { t } = useUnifiedTranslation({ namespace: 'dashboard' });
  const ts = (key: string, fallback: string): string => { const v = t(key); return typeof v === 'string' ? v : fallback; };
  const [activeTab, setActiveTab] = useState(0);

  // Загрузка метрик платформы
  const { data: metricsData, isLoading: isLoadingMetrics } = useQuery({
    queryKey: ['manager', 'platform-metrics'],
    queryFn: async () => {
      const response = await managerApi.getPlatformMetrics();
      return response.data;
    },
    refetchInterval: 60000, // Обновлять каждую минуту
  });

  // Загрузка контента для модерации
  const { data: contentData, isLoading: isLoadingContent, refetch: refetchContent } = useQuery({
    queryKey: ['manager', 'content'],
    queryFn: async () => {
      const response = await managerApi.getContent({ limit: PAGE_SIZE.DEFAULT, status: ModerationStatus.PENDING });
      return response.data;
    },
  });

  // Загрузка аналитики пользователей
  const { data: userAnalyticsData, isLoading: isLoadingUserAnalytics } = useQuery({
    queryKey: ['manager', 'user-analytics'],
    queryFn: async () => {
      const response = await managerApi.getUserAnalytics();
      return response.data;
    },
  });

  // Загрузка вовлеченности
  const { data: engagementData, isLoading: isLoadingEngagement } = useQuery({
    queryKey: ['manager', 'user-engagement'],
    queryFn: async () => {
      const response = await managerApi.getUserEngagement();
      return response.data;
    },
  });

  const registrationTrendData = useMemo(
    () => userAnalyticsData?.registrationTrend?.map((item) => ({
      ...item,
      monthLabel: item.month,
    })) ?? [],
    [userAnalyticsData?.registrationTrend]
  );

  const activityTrendData = useMemo(
    () => userAnalyticsData?.activityTrend?.map((item) => ({
      ...item,
      monthLabel: item.month,
    })) ?? [],
    [userAnalyticsData?.activityTrend]
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleContentAction = async (contentId: string, action: 'reject' | 'flag') => {
    try {
      await managerApi.performContentAction(contentId, { action });
      alert(`Контент ${action === 'reject' ? 'отклонен' : 'помечен'}!`);
      refetchContent();
    } catch (error) {
      console.error('Ошибка действия с контентом:', error);
      alert('Ошибка при выполнении действия');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {ts('manager.title', 'Панель менеджера платформы')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {ts('manager.subtitle', 'Управление платформой и контентом')}
        </Typography>
      </Box>

      {/* Метрики платформы */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('manager.kpis.totalUsers', 'Всего пользователей')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? <CircularProgress size={24} /> : metricsData?.totalUsers || 0}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    Активных: {metricsData?.activeUsers || 0}
                  </Typography>
                </Box>
                <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('manager.kpis.totalEvents', 'Всего событий')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? <CircularProgress size={24} /> : metricsData?.totalEvents || 0}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    Активных: {metricsData?.activeEvents || 0}
                  </Typography>
                </Box>
                <DashboardIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('manager.kpis.pendingModeration', 'На модерации')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingMetrics ? <CircularProgress size={24} /> : metricsData?.pendingModeration || 0}
                  </Typography>
                </Box>
                <GavelIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('manager.kpis.avgEngagement', 'Средняя вовлеченность')}
                  </Typography>
                  <Typography variant="h4">
                    {isLoadingEngagement ? <CircularProgress size={24} /> : `${((engagementData?.engagementScore || 0) * 100).toFixed(0)}%`}
                  </Typography>
                </Box>
                <TrendingUpIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Вкладки */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={ts('manager.tabs.content', 'Модерация контента')} icon={<GavelIcon />} iconPosition="start" />
            <Tab label={ts('manager.tabs.analytics', 'Аналитика')} icon={<AssessmentIcon />} iconPosition="start" />
            <Tab label={ts('manager.tabs.notifications', 'Уведомления')} icon={<NotificationsIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Вкладка: Модерация контента */}
        <TabPanel value={activeTab} index={0}>
          {isLoadingContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : contentData?.content && contentData.content.length > 0 ? (
            <DataTable
              title={ts('manager.contentTitle', 'Контент на модерации')}
              columns={[
                {
                  id: 'type',
                  label: ts('manager.columns.type', 'Тип'),
                  render: (row) => <Chip label={row.type} size="small" />,
                },
                {
                  id: 'title',
                  label: ts('manager.columns.title', 'Заголовок'),
                  sortable: true,
                },
                {
                  id: 'authorName',
                  label: ts('manager.columns.author', 'Автор'),
                },
                {
                  id: 'status',
                  label: ts('manager.columns.status', 'Статус'),
                  render: (row) => (
                    <Chip
                      label={row.status}
                      size="small"
                      color={
                        row.status === 'approved' ? 'success' :
                        row.status === 'rejected' ? 'error' :
                        row.status === 'flagged' ? 'warning' : 'default'
                      }
                    />
                  ),
                },
                {
                  id: 'priority',
                  label: ts('manager.columns.priority', 'Приоритет'),
                  render: (row) => (
                    <Chip
                      label={row.priority}
                      size="small"
                      color={
                        row.priority === 'high' ? 'error' :
                        row.priority === 'medium' ? 'warning' : 'default'
                      }
                    />
                  ),
                },
                {
                  id: 'reportCount',
                  label: ts('manager.columns.reports', 'Жалоб'),
                },
                {
                  id: 'actions',
                  label: ts('manager.columns.actions', 'Действия'),
                  render: (row: any) => (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleContentAction(row.id, 'reject')}
                        title="Отклонить"
                      >
                        <CancelIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => handleContentAction(row.id, 'flag')}
                        title="Пометить"
                      >
                        <FlagIcon />
                      </IconButton>
                    </Box>
                  ),
                },
              ]}
              data={contentData.content}
              rowKey="id"
              pagination
            />
          ) : (
            <Alert severity="success">
              {ts('manager.noContentToModerate', 'Нет контента для модерации')}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Аналитика */}
        <TabPanel value={activeTab} index={1}>
          {isLoadingUserAnalytics ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : userAnalyticsData ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('manager.analytics.userGrowth', 'Регистрации пользователей')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Новых пользователей: {userAnalyticsData.newUsers || 0}
                    </Typography>
                    {registrationTrendData.length ? (
                      <Box sx={{ height: 260, mt: 2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={registrationTrendData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="monthLabel" />
                            <YAxis allowDecimals={false} />
                            <RechartsTooltip formatter={(value: number) => value.toString()} />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="count"
                              name={ts('manager.analytics.registrations', 'Регистрации')}
                              stroke="#1976d2"
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        {ts('manager.analytics.noRegistrationTrend', 'Нет данных для построения графика')}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('manager.analytics.topCategories', 'Популярные категории')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {userAnalyticsData.topCategories?.map((cat, index) => (
                        <Chip
                          key={index}
                          label={`${cat.category} (${cat.userCount})`}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ts('manager.analytics.activityTrend', 'Активность пользователей')}
                    </Typography>
                    {activityTrendData.length ? (
                      <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={activityTrendData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="monthLabel" />
                            <YAxis allowDecimals={false} />
                            <RechartsTooltip formatter={(value: number) => value.toString()} />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="active"
                              name={ts('manager.analytics.activeUsers', 'Активные пользователи')}
                              stroke="#009688"
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        {ts('manager.analytics.noActivityTrend', 'Нет данных по активности пользователей')}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">
              {ts('manager.noAnalytics', 'Нет данных аналитики')}
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Уведомления */}
        <TabPanel value={activeTab} index={2}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Функция массовых уведомлений будет добавлена в следующей версии
          </Alert>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default ManagerDashboard;

